import { customInstance } from "@repo/api-client";
import { useActingStaff } from "./ActingStaffProvider";

/**
 * Client wiring for the PIN quick-switch + manager step-up (the SWITCH LOGIC already exists in
 * roles-and-permissions; this is the SPA half). The PIN entry dialog itself is owned by
 * roles-and-permissions and mounts into the shell header slot; it drives these calls.
 */
export interface SwitchResult {
  deviceSessionId: string;
  actingStaffId: string;
}

/** Quick-switch acting staff by PIN on an open device session (fail-closed lockout server-side, ST-6). */
export function submitPinSwitch(deviceSessionId: string, pin: string): Promise<SwitchResult> {
  return customInstance(`/api/v1/device-sessions/${deviceSessionId}/switch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pin }),
  });
}

export interface StepUpResult {
  authorizationId: string;
  expiresAt: string;
}

/**
 * Manager step-up: mint a single-use ~60s authorization the sensitive endpoint consumes (ST-4). The
 * caller passes the freshly-switched device session so the backend can attribute + lockout-guard it.
 */
export function requestStepUp(
  venueId: string,
  body: { pin: string; action: string; targetRef?: string },
  deviceSessionId?: string,
): Promise<StepUpResult> {
  return customInstance(`/api/v1/venues/${venueId}/step-up/authorizations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(deviceSessionId ? { "X-Device-Session": deviceSessionId } : {}),
    },
    body: JSON.stringify(body),
  });
}

/**
 * Hook the PIN dialog (roles-and-permissions) uses to flip acting staff: it POSTs the switch, then
 * records the result on the shared attribution context so the header badge updates. Returns the
 * switched acting-staff id so the caller can chain a step-up if the action needs one.
 */
export function useActingStaffSwitch(): (
  deviceSessionId: string,
  pin: string,
  displayName: string,
) => Promise<string> {
  const { setActingStaff } = useActingStaff();
  return async (deviceSessionId, pin, displayName) => {
    const result = await submitPinSwitch(deviceSessionId, pin);
    setActingStaff({ id: result.actingStaffId, displayName });
    return result.actingStaffId;
  };
}
